/**
 * Pure, framework-agnostic state core (M1). Every transition is
 * `(state, ...) => AppState` — no DOM, no fetch, no Preact — so the whole
 * layer unit-tests in plain Node. The UI is a projection of this state;
 * the DOM is NEVER a source of truth (the incumbent's static-pass/live-fail
 * disease is killed here, by construction).
 *
 * Invariants owned by this module:
 *  - ONE authoritative job-boundary reset (`resetForNewQuote`) — the R15 A1 /
 *    SEC-3 rule. Every job-start path goes through it; PII from customer A
 *    can never survive into customer B's job.
 *  - Monotonic quote-request sequencing (`beginQuoteRequest` /
 *    `applyQuoteResult`) — a stale response can never overwrite a newer one.
 *  - Per-booking error scoping (`bookingErrors`) — an error banner belongs to
 *    the booking that raised it and dies at the job boundary (DESIGN.md §7.3).
 *  - navSeq increments on every screen transition — the render layer's
 *    scroll-reset hook (DESIGN.md §8).
 *
 * ⚠️ THE ONE IMPORT (board 2089 M3) AND WHY IT DOES NOT BREAK THE LINE ABOVE:
 * `PHONE_DEFAULT_COUNTRY` is a pure constant out of `src/shared/phone.ts` — no
 * DOM, no fetch, no Preact — and this layer still unit-tests in plain Node. It is
 * imported rather than restated as `'IE'` because a job's default picker country
 * and the region `parseCustomerPhone` falls back to are ONE fact, and two
 * spellings of it is how a capture starts reading numbers under a country the
 * parser is not using.
 */
import { PHONE_DEFAULT_COUNTRY } from '../../shared/phone.js';

export type ScreenId =
  | 'login'
  | 'home'
  | 'location'
  | 'passengers'
  | 'addons'
  | 'quote'
  | 'done'
  | 'inbox'
  | 'bookings'
  | 'booking-detail'
  /**
   * ⛔ T1749 M6 (`R6`, collision C2) — `Flagged quotes` HAS NO RAIL ROW ANY MORE,
   * AND THIS ID STAYS ANYWAY. Alex ruled the tab hidden, "re-enable later if ever
   * needed", so what went is the ROW: the switch arm in `App.tsx` and
   * `screens/Triage.tsx` both remain, because the switch has no `default:` and an
   * id with no arm is a COMPILE error rather than a blank pane — which is the
   * warning `catalog` records below, read from the other side. Re-enabling is one
   * entry in `components/Rail.tsx`.
   * ⚠️ THE QUEUE STILL FILLS AND NOW HAS NO READER (the server coerces an unknown
   * feedback `kind` to `flag`), which is Q134 in `docs/OPEN-QUESTIONS.md`.
   */
  | 'triage'
  | 'admin'
  | 'rate-changes'
  /** T928 slice 1: promoted OUT of Admin.tsx's quiet-entry row to the rail.
   *  Both were local `View` members on the price-book screen; Alex ruled the
   *  rail should group by WHAT a thing is, and neither is a price list.
   *  ⛔ They need their own ScreenIds rather than sharing 'admin' with a
   *  sub-view, because `navigate()` returns the SAME state object when the
   *  screen id is unchanged — two rail entries pointing at one id would make
   *  the second a no-op. */
  | 'operators'
  /**
   * T939 M5 — the nursing control centre, and it REPLACES `portal-requests`
   * rather than joining it.
   *
   * Alex ruled (2026-08-10, prototype reviewed) that the rail row becomes
   * `Nursing homes`: one destination absorbing BOTH the requests queue that
   * used to live on `portal-requests` AND the nurse roster that used to be a
   * section of `operators`. The old id is gone because nothing navigates to it
   * any more — a ScreenId with no rail entry and no switch arm is a blank pane
   * waiting to be reached by a future typo.
   *
   * ⛔ THE RENAME IS ALSO WHAT KEPT THE RAIL INSIDE ITS BUDGET. `SC-10b`
   * (e2e/tests/green/tablet-ia.spec.ts) asserts the rail fits the ~700px plane
   * (`R11`). This row REPLACED the badged `Nursing home requests` row rather than
   * adding an item. ⛔ If anyone ever makes this an ADDITIONAL row, that budget
   * argument dies with it, and a live measurement is the only way to re-make it.
   * ⚠️ T1749 M6 — THE `699px` THIS NOTE CARRIED IS GONE RATHER THAN UPDATED. R6
   * re-cut the row list (`Quote tool` in, `Flagged quotes` out, `Price changes`
   * moved) and ruled every headroom figure must be re-derived before it is cited.
   * The re-derived per-row heights, the badge state and the command live in
   * DESIGN.md section 4.3, in one place.
   */
  | 'nursing-homes'
  /* ⛔ `'nursing-home-capture'` WAS HERE (the nursing tile's bespoke capture step)
     and went with its screen at board 2814 M6 (DD-8): the switch has no
     `default:`, and an id with no arm is a blank pane waiting for a typo. Its
     sibling `'invoice'` left the same way at board 2764 M6. */
  /**
   * T1749 M5 — the component catalog, and it is a DEVELOPER/REVIEW destination
   * rather than an operator one.
   * ⛔ IT IS NOT A RAIL ROW, and that is the same budget argument `nursing-homes`
   * records above: the rail clears its ~700px plane by single-digit pixels in the
   * worst badge state, so a ninth row would breach it (DESIGN.md section 4.3 has
   * the figures and the command; do not re-derive them from this comment). The
   * entry is a quiet link on the price-book shelf instead (Admin.tsx), which is
   * where the account book's entry already lives.
   * ⚠️ THE SCREEN BEHIND IT IS BUILD-SWITCHED, AND SO IS THE LINK. `scripts/
   * build-client.mjs` resolves `./screens/Catalog.js` to a null screen unless
   * `ECMB_CATALOG` is `1`, and that resolved module exports `CATALOG_PRESENT`,
   * which `App.tsx` reads to decide whether `Admin` gets an opener at all. So on a
   * default build NOTHING navigates here: the shelf renders no control for it.
   * ⛔ THE ID AND THE SWITCH ARM STAY UNCONDITIONAL, and only those two: the switch
   * has no `default:`, so an id with no arm is a COMPILE error rather than a blank
   * pane, and both postures must typecheck from one source. An unconditional LINK
   * would have been the blank pane this note warns about — it was one, for one
   * round, and the driver's pre-wave read caught it.
   */
  | 'catalog';

/** The six add-on values a job carries (mirrors the server QuoteOptions). */
export type QuoteOptions = {
  meetAndGreet: boolean;
  extraPickups: number;
  returnTrip: boolean;
  lateNight: boolean;
  trailer: boolean;
  accessibleVehicle: boolean;
};

export const EMPTY_OPTIONS: Readonly<QuoteOptions> = Object.freeze({
  meetAndGreet: false,
  extraPickups: 0,
  returnTrip: false,
  lateNight: false,
  trailer: false,
  accessibleVehicle: false,
});

/**
 * The customer's ORIGINAL stated flags — the immutable baseline the red
 * "Edited" badge compares against (R18/r16 B5: against the inquiry, never
 * against Helen's last save). null = walk-in (no customer baseline).
 */
export type CustomerFlags = Readonly<Partial<QuoteOptions>> | null;

export type PrefillSource = 'inquiry' | 'booking' | null;

/** Client projection of the server QuoteResult (shape mirrored, data always
 *  from POST /api/quote — never computed client-side). */
export type QuoteLine = { label: string; cents: number; kind: string };
export type QuoteFlag = { code: string; severity: 'info' | 'warn' | 'block'; message: string };
export type QuoteView = {
  status: 'priced' | 'estimated' | 'manual';
  lines: QuoteLine[];
  totalCents: number | null;
  flags: QuoteFlag[];
  /** Present when status === 'estimated' (anchored-place context). */
  anchoredPlace?: string;
};

/** How the current job ended up on Done (honest-state copy source).
 *  'parked' (T409) = saved for later WITHOUT a quote — no price commitment. */
export type DoneOutcome = 'saved' | 'price-check' | 'parked' | null;

/** One customer-stated middle stop (immutable statement, T400 rich shape). */
export type CustomerStop = { address: string; eircode: string | null };
/** One operator-captured middle stop ('' = not yet typed; never blocking). */
export type OperatorStop = { address: string; eircode: string };

/* ⛔ `NursingHomeCapture` (the tile's account + schedule capture) WAS HERE and went
   with the tile at board 2814 M6 (DD-8). The schedule's server half,
   `BookingSeriesCapture`, stays for GET /api/bookings/:id/series. */

/** Everything belonging to ONE job (one customer's quote). Cleared as a unit
 *  by `resetForNewQuote` — never field-by-field at call sites. */
export type JobState = {
  service: string | null;
  /** Pricing ids vs display labels are separate fields (incumbent parity). */
  locationId: string | null;
  locationLabel: string | null;
  toLocationId: string | null;
  toLocationLabel: string | null;
  band: string | null;
  options: QuoteOptions;
  venue: string | null;
  journeyDate: string | null;
  journeyTime: string | null;
  notes: string | null;
  customerName: string;
  customerPhone: string;
  /**
   * BOARD 2089 M3 (R-2) — the ISO country `customerPhone` is READ UNDER, and the
   * fourth thing the capture lifts. `'IE'` on a fresh job; an inquiry-prefilled
   * job takes the country the PUBLIC form recorded (`inquiries.extra.
   * phoneCountry`, M1), so Helen sees the number under the same rule the site
   * read it under rather than under Ireland by accident.
   *
   * ⛔ A PLAIN `string`, never `PhoneCountry`, for the same reason
   * customerName/customerPhone are strings: it is a `<select>` value bound
   * straight to a control, and every reader passes it through
   * `parseCustomerPhone`, which treats a value it does not know as the Ireland
   * default rather than as a refusal. Nothing downstream may assume it names a
   * country the metadata has.
   */
  customerPhoneCountry: string;
  /** The address a quote email WOULD go to - display only, and NOT a
   *  trusted customer-record value. Read the trust status before you use it.
   *
   *  Written twice. `sourceFromInquiry` prefills it with what the PUBLIC form
   *  typed; `refreshBookingDetail` / `reopenBooking` then overwrite it with the
   *  server's OWN recipient precedence (T549 M8, `resolveRecipientEmail`):
   *  `customers.email` first, that booking's `inquiry_email` second. So the
   *  post-load value is server-of-record in the sense that the SERVER would send
   *  there - but on the fallback branch it is still an address an
   *  unauthenticated web form supplied, merely one the server has agreed to use
   *  for this one booking. `customerEmailFromInquiry` says which branch won.
   *
   *  It is therefore NEVER a write source. Putting it in a request body launders
   *  public input through the authed capture door into `customers.email`, which
   *  is the one write M8 exists to deny (test/client/capture-email-laundering.
   *  spec.ts pins the capture POST shut; there is no operator-typed email
   *  control anywhere in the kiosk - that is Q55).
   *
   *  The overwrite lands the moment that FETCH RESOLVES, which is NOT the moment
   *  a booking exists: an in-flight or failed read leaves the raw inquiry value
   *  sitting here, so until it lands the send surface must treat this as unknown
   *  - `serverPhoneKnown` is the flag that says which, and SendQuote names no
   *  recipient until it is true. */
  customerEmail: string | null;
  /** TRUE when `customerEmail` came from this booking's own inquiry rather than
   *  the stored customer record (T549 M8). Provenance is a separate bit because
   *  the resolved address alone cannot carry it, and Helen is the only control
   *  on "is this really the customer's address?" - the send surface says so on
   *  screen. False while unknown: a display hint may never claim record status
   *  it has not confirmed, and `serverPhoneKnown` gates the whole line anyway. */
  customerEmailFromInquiry: boolean;
  /**
   * THE OPERATOR-TYPED ADDRESS (T660 M5 / P0-9, P0-10) — what Helen keyed into
   * the capture disclosure on the Quote screen, and the ONLY email-shaped value
   * in this store that may travel on a request body.
   *
   * ⛔ A SEPARATE FIELD FROM `customerEmail` ABOVE, DELIBERATELY, and merging the
   * two would re-open the exact hole M8 closed. `customerEmail` has
   * `sourceFromInquiry` as a writer — the address typed into the PUBLIC form —
   * which is why it is marked NEVER a write source. This one has no such writer:
   * `sourceFromInquiry` must never set it, `sourceFromBooking` must never set
   * it, and no fetch response writes it. It is Helen-typed or it is ''.
   *
   * '' rather than null for the same reason customerName/customerPhone are:
   * these three are the capture controls' values, bound straight to inputs, and
   * a null would render as the string "null" in one.
   *
   * Read by `createBookingForJob` only (client/lib/actions.ts). The laundering
   * pin (test/client/capture-email-laundering.spec.ts) asserts BOTH halves: that
   * this value travels, and that `customerEmail` never does.
   */
  operatorEmail: string;
  /**
   * HOW the server resolved the customer on the capture that created this
   * booking — the `matched` field of POST /api/bookings, kept rather than
   * discarded (T660 M5 / P0-12). null until a capture happens, and null forever
   * on an inquiry-sourced or re-opened job, which never runs that request.
   *
   * 'email' is the one value that means anything to the operator: the booking
   * was attached to a customer record ALREADY ON FILE for the address she typed,
   * rather than to a new one. Where the typed phone contradicted that, the
   * server refused before writing anything (P0-12); this is the residue it
   * cannot judge — no phone, or a phone nobody owns — and Done states it so the
   * re-attach is never silent. It NAMES NOBODY, for the reason
   * CustomerEmailConflictError does not either.
   */
  customerMatchedBy: 'email' | 'phone' | 'created' | null;
  customerEircode: string | null;
  /** T484 — the customer's stated contact preference, carried from the inquiry
   *  for DISPLAY only (CustomerInfoBlock; T549 M7 also DEFAULTS the send
   *  channel from it). Never edited here and never written back: the booking row
   *  already holds it (normalise threads it), so adding a write path would need
   *  a persistQuote reconcile it does not have. A per-send channel override is
   *  therefore component state and never touches this field. */
  contactPreference: string | null;
  flightNumber: string | null;
  /** Board 944 - the RETURN leg's own pax/date/time. DISPLAY only, exactly
   *  the same contract as flightNumber above: never edited here, never
   *  written back. Rendered as ONE composed Return row in CustomerInfoBlock,
   *  not three rows - Helen bounced off a tool with too many buttons.
   *  ⭐ board 2871: `returnDate`, when a real calendar day, is also the DEFAULT
   *  day the Quote screen's Return date shows for Helen to confirm. It is never
   *  written unless she types (`returnDraft`); `returnTime` is never a default. */
  returnPassengers: string | null;
  returnDate: string | null;
  returnTime: string | null;
  /**
   * ⭐⭐ T1717 M6 (DD5, P0-7) - THE PORTAL LANE'S RETURN INSTANT, and the
   * asymmetry with the three fields above is deliberate rather than untidy.
   *
   * Those three are FREE TEXT the public site form captured; this is a
   * VALIDATED ISO instant a nurse stated on the portal, which crossed the seam
   * and landed on `bookings.return_at`. The Return row prefers this one when it
   * is present and falls back to the site strings otherwise, so a web enquiry
   * renders exactly as it did before this milestone.
   *
   * ⛔ STILL NEVER EDITED IN PLACE: it is the STORED value, and Helen's typing
   * lives in `returnDraft` below. ⭐ board 2871 SUPERSEDES the old "the kiosk has
   * no return-time control at all (DD6)" line: DD6 was T1717's scope note ("the
   * boolean only"), not a ban, and Helen asked for the control (voice note, 7 Oct).
   * The Quote screen's Return date and Return time now write `bookings.return_at`
   * through the quote and park doors. A nursing home's stated return is not
   * overwritten by it: nursing jobs never reach Quote, and both doors refuse
   * every account, portal-linked or nursing booking first.
   * ⛔ RENDER IT ONLY WITH `options.returnTrip` SET - a job the operator has set
   * to one way must stop showing a return time, without the home's answer being
   * erased underneath her.
   */
  returnAt: string | null;
  /**
   * ⭐ board 2871 - WHAT HELEN TYPED in Return date / Return time, or null until
   * she types. Null sends nothing (a re-save never blanks a stored return), so
   * the defaults shown before she types (the stored return, the customer's stated
   * day from the site form, the journey date) are never written on their own. A
   * null `day` takes those defaults (`shownReturnLeg`, client/lib/actions.ts).
   */
  returnDraft: { day: string | null; time: string } | null;
  /**
   * ⭐⭐ T2088 M7 (plan D5; ADR-0006) - **WHY** THERE IS NO INSTANT, so the card
   * that shows Helen a DOUBLED figure can say what the second half is waiting on.
   *
   * ⛔ DISPLAY ONLY, THE SAME CONTRACT AS `returnAt` ABOVE: the kiosk has no
   * control that sets it, nothing writes it back, and it is never a price input -
   * D5 already ruled that a to-confirm return is priced as a FULL return, so the
   * doubling happens off `options.returnTrip` exactly as before and this field
   * changes no arithmetic anywhere.
   * ⛔ `'unset'` IS THE HONEST DEFAULT AND THE ONLY ONE ANY OTHER DOOR PRODUCES.
   * It is the value of every web enquiry, every walk-in, every job opened from
   * the Inbox, and every portal job whose home either gave a time or never had
   * one to give. Only the BOOKING prefill can carry `'to-confirm'`, off the
   * detail route's `portalRequest`.
   * ⚠️ NOT NULLABLE, unlike `homeReturnTrip` below, and the difference is real:
   * that field needs a third state because "no home answered" must not read as
   * "the home said one way". This one does not - a job with no portal answer has
   * no marker to confirm, which IS `'unset'`, and a null would only give every
   * render a second way to spell silence.
   */
  returnAtState: 'unset' | 'to-confirm';
  /**
   * ⭐⭐ T1717 M6 (DD6, P0-11) - WHAT THE **HOME** SAID about the return, or null
   * when this job did not come from the portal.
   *
   * ⛔ IT IS NOT `customerFlags.returnTrip` AND IT IS NOT REDUNDANT WITH IT.
   * `customerFlags` is frozen from whatever seeded the job: on the RE-OPEN door
   * that is `bookings.return_trip`, which `updateBookingFlags` overwrites on
   * every quote save - so after Helen's first override the "customer baseline"
   * IS her own value and the nurse's answer is gone. This field is read from
   * `inquiries.return_trip`, which the crossing writes once and nothing updates,
   * so it survives every save.
   *
   * ⛔ NULL IS "NOBODY FROM A HOME ANSWERED", NOT "the home said one way". A
   * three-state fact needs three states: a boolean would make every web enquiry
   * look like a nursing home that asked for a one-way run, and the screen would
   * announce an override against an answer nobody gave.
   */
  homeReturnTrip: boolean | null;
  recurring: boolean;
  /** Free text carried from an inquiry whose pickup/drop-off had no single
   *  exact gazetteer match — pre-types the location search, never auto-picks
   *  (resolveLocationExact single-exact-match-only parity). */
  pickupSearchText: string | null;
  dropoffSearchText: string | null;
  /** T515 — an ARRIVALS journey's pickup end is an airport TERMINAL: a place
   *  the gazetteer will never hold, while the PRICED location sits on the
   *  address (drop-off) end. locationId/locationLabel is one pair serving two
   *  jobs — the priced grid location AND the pickup end's display label — and
   *  they coincide for every service except from-airport. This field is that
   *  separation: non-null ONLY on from-airport, so every read site is
   *  byte-identical elsewhere. Read it through `pickupEndPlace` (selectors),
   *  never bare. Deliberately NOT cleared by setPickup: re-pricing the town
   *  must not move the terminal end. */
  pickupTerminal: string | null;
  /** The place is off the gazetteer and Helen chose to hand-quote it (T373 M4).
   *  The place text itself lives in locationLabel/toLocationLabel (so it flows to
   *  booking_stops + journeySummary); locationId stays null (the engine returns
   *  status:'manual'). These per-end markers are what let the readiness gate
   *  treat the end as ready DESPITE the null id. Per-end, because a singular
   *  field cannot say "the DROP-OFF is off-gazetteer". */
  pickupOffGazetteer: boolean;
  dropoffOffGazetteer: boolean;
  /**
   * T906 S1 — the two ENDS of a golf journey, as Helen actually stated them.
   *
   * ⛔ THEIR OWN FACT, DELIBERATELY SEPARATE FROM THE RATE KEY. The obvious
   * cheaper shapes are both wrong and were both ruled out at source:
   *   - reusing `setPickupOffGazetteer` writes the place into `locationLabel`
   *     with a null id, and on golf `locationLabel` IS the composite rate
   *     label — so it destroys the price (engine/quote.ts gates the grid branch
   *     on `locationId !== null`) AND nulls `golfCourseEnds`, leaving the job
   *     with no destination row at all.
   *   - minting a synthetic '<P> — → <D>' composite into `locationLabel` makes
   *     `precedentPlace` (store/selectors.ts) key precedent lookups on a
   *     fabrication. A composite is rate bookkeeping; it is not the journey.
   * So `locationId`/`locationLabel` keep today's meaning byte-for-byte — the
   * STORED bytes of a picked rate row, or null — and these carry the journey.
   *
   * Both null on every non-golf service, and on a golf job re-opened from a
   * booking written before this shipped (whose ends are recovered from its
   * persisted stops instead). Read them through `pickupEndPlace` /
   * `dropoffEndPlace`, never bare: that is the single source the persist path,
   * the team alert and both display surfaces share, so they cannot drift into
   * naming three different pickups.
   */
  golfPickupPlace: string | null;
  golfDestinationPlace: string | null;
  /** Golf course free text from the inquiry (journey context display). */
  courseText: string | null;
  /** T907 — the golf COLLECTION PLACE the customer named on the website: which
   *  of our priced places they set off from, the other end of the golf pair.
   *  Display-only here, and distinct from the pickup ADDRESS, which the
   *  pickup/town/eircode trio still owns and which still drives the driver. */
  collectionPlace: string | null;
  /** Customer-stated MIDDLE stops (inquiry stop1..4 / booking_stops middles),
   *  display-only, now carrying the stated eircode (T400 — previously the
   *  eircode was dropped at prefill and could never round-trip). This is the
   *  customer's statement, NEVER the billable count — options.extraPickups is
   *  Helen's decision and must never be derived from this array (369
   *  customerFlags lesson: record the statement immutably, let Helen decide
   *  the billed value). */
  customerStops: readonly CustomerStop[];
  /** Helen's editable per-stop capture rows (T400) — the WORKING middle-stop
   *  set. Seeded from the customer statement at prefill; the rows the Addons
   *  screen renders when extraPickups > 0. What persists: journeyStops() sends
   *  operatorStops when Helen's count > 0, else the statement pass-through. */
  operatorStops: readonly OperatorStop[];
  /** T400 per-END capture (Eircode-first, optional, never blocking, never a
   *  priced input): the driver-facing street line + Eircode for each end.
   *  The place label stays in locationLabel/toLocationLabel — these fields
   *  are the v16 address_line/eircode slots, kept OUT of the quote body. */
  pickupEircode: string;
  pickupAddressLine: string;
  dropoffEircode: string;
  dropoffAddressLine: string;
  /** Resolved-town carry for UNRESOLVED ends on re-open (v16 town slot) — a
   *  resolved end re-derives its town from locationLabel; these keep the
   *  stored town from being nulled when the end no longer exact-resolves. */
  pickupTown: string | null;
  dropoffTown: string | null;
  /** Pre-types the Location search when an inquiry carried a structured town
   *  (T384 extra.town — the ADDRESS end's town whichever the direction).
   *  Display-grade seed only: the Journey line keeps rendering the raw
   *  pickup/dropoff texts in their true slots, so a from-airport journey
   *  still reads airport-first. */
  locationSearchSeed: string | null;
  /** Eircode routing key from the inquiry's ADDRESS-end Eircode (T385 item
   *  5) — a suggestion-grade AREA hint for the location search (A63 spans
   *  6+ towns). Display/ordering only: never a resolver, never an
   *  auto-pick, never removes results. */
  routingKey: string | null;
  customerFlags: CustomerFlags;
  /**
   * The customer's ORIGINAL stated band — the immutable baseline the band's
   * "Edited" badge compares against, mirroring `customerFlags` exactly.
   * null = walk-in (no customer baseline → no badge is possible).
   *
   * No `Object.freeze`: it is a primitive, so there is no object to freeze.
   * The `Omit` in `patchJob` rejects a direct literal write at COMPILE time
   * (TS's excess-property check) — it is a guard, not a guarantee: a patch
   * widened through a `Partial<JobState>` variable would still get through.
   * The behavioural pin is the test "a band pick never moves the baseline".
   *
   * On re-open (`sourceFromBooking`) the baseline is the PERSISTED band, just
   * as `customerFlags` takes the persisted flags: this mirrors the shipped
   * behaviour rather than inventing a third semantic.
   */
  customerBand: string | null;
  /** Persistence linchpin: null = walk-in (lazy-create on Accept with a name). */
  bookingId: number | null;
  /** ⭐ board 2814 M7 (DD-7) - the re-opened booking was passed to a taxi firm, so
   *  the team alert is hidden. OPTIONAL: absent (every new job) reads as not passed. */
  taxiPassed?: boolean;
  inquiryId: number | null;
  suggestedDuplicateId: number | null;
  finalTotalCents: number | null;
  finalNote: string | null;
  wasHandQuoted: boolean;
  /**
   * ⭐ T1949 (board ask 823) — THE AMOUNT A BAND CHANGE JUST TOOK AWAY, so the
   * Quote screen can say so. Ruling 804 lets a corrected count re-derive a
   * pinned band, and that re-derivation runs through `setBand`'s band-change
   * carry, which clears an agreed hand price. Alex ruled: KEEP the clear, and
   * ANNOUNCE it naming the amount, so nothing vanishes silently.
   *
   * ⛔ DISPLAY ONLY. It is never persisted, never sent to the server and never
   * read by a price path — it exists so the screen can name a number the job
   * no longer holds. `null` means "nothing to announce", which is also the
   * state after a band change that cleared nothing.
   *
   * Written by exactly two places (`setBand` sets it, `persistCurrentQuote`
   * nulls it when a new price is agreed) and reset at every job boundary
   * through `EMPTY_JOB`. `applyQuoteResult` spreads `...state.job`, so a
   * re-price carries it rather than dropping it.
   */
  handPriceClearedCents: number | null;
  depositSent: boolean;
  depositStale: boolean;
  prefillSource: PrefillSource;
  /** The latest applied engine quote (display truth for lines/total/flags). */
  quote: QuoteView | null;
  /** Set when Accept/Override persisted (or honestly didn't) — Done copy. */
  doneOutcome: DoneOutcome;
  /** The amount this booking was LAST quoted at (re-open context only). It
   *  pre-fills the hand-price field so Helen adjusts rather than re-keys;
   *  it is never itself a price (the typed value is). */
  previousQuoteCents: number | null;
  /** SERVER-OF-RECORD recipient (booking detail customer.phone_e164) — the
   *  send UI displays THIS, never the typed phone (SEC-6). null means
   *  UNKNOWN until serverPhoneKnown is true (unknown is not absent). */
  serverPhone: string | null;
  serverPhoneKnown: boolean;
  /** T660 M4 (plan step 4) — TRUE when the booking-detail read FAILED, as
   *  opposed to being still in flight. `serverPhoneKnown` cannot tell those
   *  apart (it is false in both), there is no retry on this path, and nothing
   *  re-runs the read for the rest of the job — so without this the send block
   *  claimed "getting the contact details..." forever. Written only by
   *  refreshBookingDetail (set in its catch, cleared on every success). */
  serverDetailFailed: boolean;
  /** Latest send-ledger row for this booking (G4) — the send UI's delivery
   *  truth (queued/sent/delivered/failed), never an optimistic 'sent'.
   *  `target` (T660 M4, P0-7) is WHERE that message went, off the ledger row.
   *  OPTIONAL: hand-built job fixtures across the suite predate it, and an
   *  absent value must read as "not recorded" (the sentence omits the
   *  recipient), never as `undefined` on screen. */
  latestSend: { channel: string; deliveryStatus: string; target?: string | null } | null;
  /** Latest payment status from the poll (deposit_sent/deposit_paid/...). */
  paymentStatus: string | null;
  depositUrl: string | null;
  depositAmountCents: number | null;
  /* ⛔ `nursingHome` and `seriesNotRecorded` WERE HERE: the tile's capture and the
     Done notice for a schedule that did not save. Both went with the tile at board
     2814 M6 (DD-8); the client sends no schedule, so nothing can set either. Job
     state is never stored in the browser (scripts/check-copy.sh bans storage in
     client source; its SINK_ALLOW admits only the boot sweep
     `purgeLegacyNursingCaptures`, which clears the older stored blobs), so no
     saved job can carry either field back. */
  /**
   * T651 M7 (R21) — the REAL headcount, as Helen states it. null = not stated,
   * which is the honest default and stays the value on every job nobody asks
   * about.
   *
   * `band` is the pricing key (rates.band) and stays it; `bookings.passengers`
   * stays the band-derived CEILING. This is the dispatch/fleet number - the only
   * way to answer "how many people did we actually carry".
   *
   * ⛔⛔ AMENDED, T1871 M5 (board #1871). THIS DOCBLOCK USED TO READ "NOT A
   * PRICING INPUT AND NEVER PAIRED WITH A RE-PRICE ... its writer is a plain
   * patch, never setBand's paired writer", and that is NO LONGER TRUE. The
   * amendment is deliberate and lands in the same change as the code, because a
   * shipped comment forbidding what the shipped code does is worse than no
   * comment.
   *
   * WHAT CHANGED: board #1871 makes the count the thing Helen actually knows
   * ("there are 17 of us"), and a kiosk that captures 17 while offering only the
   * four priced band tiles is the dead end that row exists to fix. So a changed
   * count now RE-DERIVES the band through `bandForCount`, and because band is a
   * priced input that re-derivation goes through `setBand`, the paired writer -
   * which re-prices. The count therefore CAN move the money now, indirectly.
   *
   * ⛔⛔ AND IT RE-DERIVES FROM A SETTLED COUNT ONLY (T1871 M7, MERGE BLOCKER).
   * "A changed count" above was true of every KEYSTROKE, and that was a measured
   * money defect: a two-digit headcount is typed as a one-digit count first, and
   * the transient prefix derived a priced band and cleared the operator's agreed
   * hand price, which the second digit could not undo. The writer therefore
   * SPLIT - `setPassengersExact` writes this field on every keystroke and
   * derives nothing; `commitPassengersExact` (wired to the count box's blur)
   * carries the derivation. Both are in `client/lib/actions.ts` and the account
   * is on them. ⭐ THIS FIELD'S OWN CONTRACT IS UNCHANGED by that split: it is
   * still written live, so everything reading its VALUE mid-edit (P0-3's
   * over-band state, ask 805's save door) still sees the truth.
   *
   * ⛔ WHAT DID NOT CHANGE, and both halves matter:
   *  - The count is still never sent to `/api/quote`. `band` remains the only
   *    passenger fact the engine sees; the count moves money only by moving the
   *    band, never on its own.
   *  - The re-derivation yields to `bandPinnedByOperator` ONLY WHERE IT DERIVES
   *    THE OVER-BAND TOKEN. ⛔ AMENDED BY BOARD ASK 804, answered "Re-derive
   *    when the count fits a priced band": this clause used to read "the
   *    re-derivation yields to `bandPinnedByOperator`" without qualification,
   *    and that is no longer what `setPassengersExact` does. A count that fits
   *    one of the four priced bands re-derives THROUGH her pick; her pick still
   *    outranks a count no band fits (see that field).
   *  - It is still OPTIONAL, and `null` still means NOT CAPTURED - never
   *    "unverified", never a lesser booking. It is NULL on every pre-M3 row and
   *    on every nursing-home crossing by decision, so nothing may gate a screen,
   *    a transition or an action on its presence, and nothing may derive a
   *    confidence or completeness flag from it. ⚠️ `Number(null) === 0` and
   *    `Number('') === 0`: test it with `!= null`, never truthiness.
   */
  passengersExact: number | null;
  /**
   * ⭐ HELEN PICKED THIS BAND BY HAND, AND IT OUTRANKS THE COUNT (T1871 M5).
   *
   * Set by the operator's own band act - a Passengers tile, a Quote chip, or the
   * over-band forward edge - and cleared ONLY by `resetForNewQuote`, the one
   * authoritative job boundary. Nothing else writes it.
   *
   * ⛔ IT RECORDS AN ACT; IT IS NEVER DERIVED, and both refuted alternatives are
   * worth keeping because a later reader will re-propose them:
   *
   *  1. `bandForCount(passengersExact) !== band` (a comparison of two values
   *     that both move) CANNOT tell "never pinned" from "wandered into
   *     agreement". Helen types 20, deliberately taps `12-16` (two minibuses,
   *     not a coach); the party shrinks to 14, whose derived band EQUALS
   *     `12-16`, so the flag silently reads unpinned; the party grows back to 20
   *     and her deliberate pick is overwritten - and via the paired writer that
   *     re-prices the job.
   *     ⛔ HALF THIS SCENARIO IS NOW RULED IN, AND THE REFUTATION STILL STANDS
   *     (board ask 804, "Re-derive when the count fits a priced band"). A count
   *     that derives a DIFFERENT PRICED band now does move the band through her
   *     pick, by ruling - so "the pin wins for the rest of the job" is no longer
   *     the rule, and a reader must not take this paragraph for one. What the
   *     ruling did NOT do is derive the flag: the last step above, 20 growing
   *     back to an over-band derivation, is exactly where the pin still wins,
   *     and it is the step a comparison-derived flag gets wrong. The flag
   *     therefore still has to record an ACT. It is also why 804 does not clear
   *     the flag on a machine re-derive: un-pinning on agreement IS this
   *     alternative, arrived at by a different route.
   *  2. Anything read off the count's PRESENCE is illegal outright - the exact
   *     count-as-trust-signal the board row forbids, and it would now read
   *     "pinned" on every re-opened booking that HAS a stored count.
   *     ⚠️ THE OLD WORDING OF THIS CLAUSE RESTED ON `sourceFromBooking` leaving
   *     the count null on re-open. That stopped being true at the M6 ux-review
   *     (the count IS restored now, as a fact), so the reason is restated from
   *     the rule itself rather than from an implementation detail that moved.
   *     The flag still records an ACT, and a re-open is not one: it comes back
   *     false however many people the booking carries.
   *
   * ⛔ Component-local state cannot serve: `BandControl` is mounted only on
   * Quote and already holds `raw`/`exactError` as `useState`, so every
   * Passengers <-> Quote trip would destroy it.
   *
   * ⚠️ Unlike `customerBand`/`customerFlags` this IS patchable, and that is not
   * an oversight: those two are the CUSTOMER's immutable statement, this is
   * Helen's live decision during the job.
   */
  bandPinnedByOperator: boolean;
};

export const EMPTY_JOB: Readonly<JobState> = Object.freeze({
  service: null,
  locationId: null,
  locationLabel: null,
  toLocationId: null,
  toLocationLabel: null,
  band: null,
  options: EMPTY_OPTIONS,
  venue: null,
  journeyDate: null,
  journeyTime: null,
  notes: null,
  customerName: '',
  customerPhone: '',
  // Board 2089 M3: Ireland by default, which is `PHONE_DEFAULT_COUNTRY` — the
  // same region `parseCustomerPhone` falls back to — named through the shared
  // module so the picker's default and the parser's can never drift apart.
  customerPhoneCountry: PHONE_DEFAULT_COUNTRY,
  customerEmail: null,
  customerEmailFromInquiry: false,
  operatorEmail: '',
  customerMatchedBy: null,
  customerEircode: null,
  contactPreference: null,
  flightNumber: null,
  returnPassengers: null,
  returnDate: null,
  returnTime: null,
  returnAt: null,
  returnDraft: null,
  returnAtState: 'unset',
  homeReturnTrip: null,
  recurring: false,
  pickupSearchText: null,
  dropoffSearchText: null,
  pickupTerminal: null,
  pickupOffGazetteer: false,
  dropoffOffGazetteer: false,
  golfPickupPlace: null,
  golfDestinationPlace: null,
  courseText: null,
  collectionPlace: null,
  customerStops: [],
  operatorStops: [],
  pickupEircode: '',
  pickupAddressLine: '',
  dropoffEircode: '',
  dropoffAddressLine: '',
  pickupTown: null,
  dropoffTown: null,
  locationSearchSeed: null,
  routingKey: null,
  customerFlags: null,
  customerBand: null,
  bookingId: null,
  inquiryId: null,
  suggestedDuplicateId: null,
  finalTotalCents: null,
  finalNote: null,
  wasHandQuoted: false,
  handPriceClearedCents: null,
  depositSent: false,
  depositStale: false,
  prefillSource: null,
  quote: null,
  doneOutcome: null,
  previousQuoteCents: null,
  serverPhone: null,
  serverPhoneKnown: false,
  serverDetailFailed: false,
  latestSend: null,
  paymentStatus: null,
  depositUrl: null,
  depositAmountCents: null,
  passengersExact: null,
  bandPinnedByOperator: false,
});

export type AppState = {
  operator: string | null;
  screen: ScreenId;
  /** Back-history of ACTUAL visits (R17 P2-1: Back retraces what Helen saw). */
  navStack: readonly ScreenId[];
  /** Increments on every transition — the scroll-reset hook (§8). */
  navSeq: number;
  job: JobState;
  /** Monotonic quote-request counter (racing-guard parity). */
  quoteRequestSeq: number;
  /** Seq of the quote result currently displayed; results older than the
   *  latest issued request are discarded by `applyQuoteResult`. */
  appliedQuoteSeq: number;
  /** Badge single source (§7.5): null = not yet derived. The rail badge and
   *  the home tile BOTH read this one field — they can never disagree. */
  inboxCount: number | null;
  /** True when the last badge derivation failed — renders as a distinct
   *  error badge, never as a silent zero ("never understates"). */
  inboxCountError: boolean;
  /** Rate-conflict count for the rail's Price-changes badge (same
   *  distinct-error semantics — the R13 reconcile lesson). */
  rateChangesCount: number | null;
  rateChangesError: boolean;
  /** T928 slice 1 — how many nursing-home requests are still waiting on Helen
   *  (`status === 'new'`), for the rail's Nursing-home-requests badge. Same
   *  distinct-error semantics as the two above, and here the reasoning is
   *  already written down one layer below: `listPortalRequests` refuses to
   *  degrade a dead seam to an empty list, because "nothing is waiting" is a
   *  MEANING on this surface. A silent zero here would re-introduce exactly the
   *  ambiguity that action removes. */
  portalCount: number | null;
  portalCountError: boolean;
  /** Error copy scoped to the booking that raised it (§7.3). */
  bookingErrors: Readonly<Record<number, string>>;
  /** Browse context: which booking the detail screen shows (not job state —
   *  browsing a detail never touches the in-flight quote). */
  viewBookingId: number | null;
  /**
   * ⭐ board 2812 M4 (plan step 2) — WHICH HOME'S PAGE NURSING HOMES SHOWS, by its group
   * key (Q240: the account's short code, or `a<account id>`; never a portal home id),
   * or `null` for All homes. Browse context beside `viewBookingId`: picking a home is
   * NOT a history step (like a tab), it SURVIVES the trip to a booking so Back returns
   * to the home, and it is CLEARED on log out and on any move to a screen that is
   * neither Nursing homes nor a booking (`keepHome`), so a later visit by the rail
   * opens on All homes.
   */
  viewHomeKey: string | null;
  /**
   * ⭐ board 2812 fix round (Captain's /check item 8) — A HOME'S DETAILS IS OPEN on
   * Nursing homes, in place of its tab's list. Held HERE, beside `viewHomeKey`, and
   * not in the screen, because the global Back reads it: Back from Details closes
   * Details and lands on that home's page and the tab it came from (`goBack`), where
   * before it popped the visit history past the home (opening a home and its Details
   * are not history steps) and landed on the Quote tool. Cleared by any move to
   * another screen (`navigate`), by Home, by the rail's entry and by log out.
   */
  viewHomeDetails: boolean;
  /* ⛔ NO `invoiceSource` (board 2764 M6, R-12). It held what the kiosk's own
     statement screen rendered, and both are gone. */
};

export function initialState(): AppState {
  return {
    operator: null,
    screen: 'login',
    navStack: [],
    navSeq: 0,
    job: { ...EMPTY_JOB, options: { ...EMPTY_OPTIONS } },
    quoteRequestSeq: 0,
    appliedQuoteSeq: 0,
    inboxCount: null,
    inboxCountError: false,
    rateChangesCount: null,
    rateChangesError: false,
    portalCount: null,
    portalCountError: false,
    bookingErrors: {},
    viewBookingId: null,
    viewHomeKey: null,
    viewHomeDetails: false,
  };
}

/** ⭐ board 2812 M4: a home's page survives Nursing homes and a booking opened from it,
 *  and nothing else. */
function keepHome(screen: ScreenId, key: string | null): string | null {
  return screen === 'nursing-homes' || screen === 'booking-detail' ? key : null;
}

// ---------------------------------------------------------------------------
// Navigation
// ---------------------------------------------------------------------------

export function navigate(
  state: AppState,
  screen: ScreenId,
  opts: { track?: boolean } = {},
): AppState {
  if (screen === state.screen) return state;
  const track = opts.track ?? true;
  return {
    ...state,
    screen,
    navStack: track ? [...state.navStack, state.screen] : state.navStack,
    navSeq: state.navSeq + 1,
    viewHomeKey: keepHome(screen, state.viewHomeKey),
    viewHomeDetails: false,
  };
}

/** Pop the history stack — Back retraces actual visits (R17 P2-1). On an
 *  empty stack, Back goes home (resume-quote parity: first Back = home).
 *  ⭐ board 2812 fix round (item 8): on a home's open Details, Back first closes
 *  Details and stays on that home's page (its tab), a step of its own. */
export function goBack(state: AppState): AppState {
  if (state.screen === 'nursing-homes' && state.viewHomeDetails) {
    return { ...state, viewHomeDetails: false, navSeq: state.navSeq + 1 };
  }
  const prev = state.navStack[state.navStack.length - 1];
  if (prev === undefined) {
    return state.screen === 'home' || state.screen === 'login'
      ? state
      : { ...state, screen: 'home', navSeq: state.navSeq + 1, viewHomeKey: null, viewHomeDetails: false };
  }
  return {
    ...state,
    screen: prev,
    navStack: state.navStack.slice(0, -1),
    navSeq: state.navSeq + 1,
    viewHomeKey: keepHome(prev, state.viewHomeKey),
    viewHomeDetails: false,
  };
}

/** One-hop home from anywhere (the R15 minibus-home behaviour, wordmark now).
 *  Clears the history stack; the mid-quote job survives (rail-safe, §4.1). */
export function goHome(state: AppState): AppState {
  if (state.screen === 'home') return state;
  return { ...state, screen: 'home', navStack: [], navSeq: state.navSeq + 1, viewHomeKey: null, viewHomeDetails: false };
}

// ---------------------------------------------------------------------------
// Session
// ---------------------------------------------------------------------------

export function login(state: AppState, operator: string): AppState {
  return {
    ...resetForNewQuote(state),
    operator,
    screen: 'home',
    navStack: [],
    navSeq: state.navSeq + 1,
  };
}

/** Logout is a job boundary (one of the three incumbent reset points). */
export function logout(state: AppState): AppState {
  return {
    ...resetForNewQuote(state),
    operator: null,
    screen: 'login',
    navStack: [],
    navSeq: state.navSeq + 1,
    inboxCount: null,
    inboxCountError: false,
    rateChangesCount: null,
    rateChangesError: false,
    portalCount: null,
    portalCountError: false,
    viewBookingId: null,
    viewHomeKey: null,
    viewHomeDetails: false,
  };
}

// ---------------------------------------------------------------------------
// Job boundary — THE one authoritative reset (SEC-3 / R15 A1)
// ---------------------------------------------------------------------------

/**
 * Clears EVERY job field as a unit. All job-start paths call this — never
 * clear fields ad hoc at a call site (the incumbent's A1 corruption came from
 * a second, incomplete field list). Also clears per-booking error scope
 * (§7.3: error state dies at the job boundary).
 */
export function resetForNewQuote(state: AppState): AppState {
  // Invalidate any in-flight quote response from the PREVIOUS job: bumping the
  // seq means a late job-A response can never land in job B's state (the guard
  // in `applyQuoteResult` compares against quoteRequestSeq, which this bumps).
  //
  // Both counters move together so a reset is a SETTLED state. Bumping only the
  // request seq would leave req = applied + 1 with nothing in flight, so
  // `quotePending` would read true on a brand-new job — and the in-flight save
  // guard (persistCurrentQuote) would then refuse every legitimate save that
  // never calls fetchQuote at all: an unresolved-location prefill, a re-open
  // with an unknown descriptor, and fetchQuote's own early-return when there is
  // no service or band. Settling here is what keeps those saves working.
  const seq = state.quoteRequestSeq + 1;
  return {
    ...state,
    job: { ...EMPTY_JOB, options: { ...EMPTY_OPTIONS } },
    bookingErrors: {},
    quoteRequestSeq: seq,
    appliedQuoteSeq: seq,
  };
}

/** Service-tile tap: fresh job, then the chosen service (incumbent parity —
 *  tile tap is a job-start point and runs the SAME reset). */
export function startJob(state: AppState, service: string): AppState {
  const fresh = resetForNewQuote(state);
  return { ...fresh, job: { ...fresh.job, service } };
}

// ---------------------------------------------------------------------------
// Prefill sources (pure — M4 wires the full field completeness)
// ---------------------------------------------------------------------------

/** Subset of an inquiry the store consumes (field names mirror the API). */
export type InquiryPrefill = {
  inquiryId: number;
  bookingId: number | null;
  service: string;
  band: string | null;
  name: string;
  phone: string;
  /** BOARD 2089 M3 — the ISO country the PUBLIC form recorded for this lead
   *  (`inquiries.extra.phoneCountry`, M1), so the kiosk reads the number under
   *  the same rule the site read it under. OPTIONAL and nullable for the reason
   *  the four fields below it are: several specs build full `InquiryPrefill`
   *  literals and a required key would red every one. Absent or null means "not
   *  stated" and the job keeps its Ireland default. */
  customerPhoneCountry?: string | null;
  email: string | null;
  eircode: string | null;
  /** T484 — stated contact preference (raw stored value; may be a RETIRED one
   *  on an older lead, so the renderer labels it, never this carrier). */
  contactPreference: string | null;
  flightNumber: string | null;
  recurring: boolean;
  journeyDate: string | null;
  journeyTime: string | null;
  venue: string | null;
  notes: string | null;
  course: string | null;
  collectionPlace: string | null;
  options: Partial<QuoteOptions>;
  suggestedDuplicateId: number | null;
  /** Resolved gazetteer picks (exact-match-only; null = unresolved). */
  location: { id: string; label: string } | null;
  toLocation: { id: string; label: string } | null;
  /** Raw inquiry text for unresolved locations — pre-types the search. */
  pickupText: string | null;
  dropoffText: string | null;
  /** Customer-stated middle stops (T400: rich — address + stated eircode). */
  stops: readonly CustomerStop[];
  /** T400 per-END stated eircodes (inquiry pickup_eircode / dropoff_eircode). */
  pickupEircode: string | null;
  dropoffEircode: string | null;
  /** T486 — the customer's typed STREET for the address end: the composed
   *  "street, town" with the town stripped back off (see inquiryAddressLines).
   *  Null on the terminal end, whose place label already names it. Seeds the
   *  driver-details capture so Helen never retypes what the lead already gave. */
  pickupAddressLine: string | null;
  dropoffAddressLine: string | null;
  /** T400 — the ADDRESS end's structured town (extra.town), direction-mapped
   *  by the caller. Seeds the town CARRY so an end that stays off-gazetteer
   *  (escape hatch) persists the same town normalise wrote instead of wiping
   *  it (two-writer symmetry; resolved ends re-derive from the label). */
  pickupTown: string | null;
  dropoffTown: string | null;
  /** T515 — the ARRIVALS terminal (see JobState.pickupTerminal). OPTIONAL: five
   *  specs build full InquiryPrefill literals, and the only producer is
   *  quoteThisInquiry, so omission stays byte-identical. */
  pickupTerminal?: string | null;
  /** Board 944 — the RETURN leg's own pax/date/time (extra.return*), captured
   *  because "sometimes the details can be different". OPTIONAL for the same
   *  reason as pickupTerminal above, and it is now SIX specs that build full
   *  InquiryPrefill literals (store, capture-email-laundering, quote-edit-in-
   *  place, lead-context, inbox, shell) — required fields would red all six. */
  returnPassengers?: string | null;
  returnDate?: string | null;
  returnTime?: string | null;
  /** ⭐ T1717 M6 - `extra.returnAt`, the portal crossing's own long-tail key
   *  (read with the same helper as `extra.flight`). OPTIONAL for the same
   *  reason as the three above: several specs build full `InquiryPrefill`
   *  literals and a required field would red every one. */
  returnAt?: string | null;
  /**
   * ⭐ T1717 M6 (DD6) - what the HOME said, when this door can know it.
   *
   * ⚠️ NO PRODUCER SETS IT TODAY, AND THAT IS A MEASURED FACT RATHER THAN AN
   * OVERSIGHT (recorded in the M6 implementer record with its trigger). The
   * kiosk inbox projection (`INQUIRY_COLUMNS`, core/data/inquiries-repo.ts)
   * carries no `source` column, so this lane cannot tell a portal row from a web
   * one. The carrier exists so both prefill doors have the same shape and the
   * seeding rule below is written once.
   *
   * ⛔⛔ CORRECTED, T1746 M1 (B1). This paragraph used to give the REASON as
   * "the portal crossing NORMALISES its inquiry to a booking inside the same
   * handler, so a portal-sourced request never sits in the inbox waiting to be
   * quoted". THAT WAS FALSE, and measurably so: the crossing is TWO
   * transactions, so a landing whose booking is refused (a `bookings_job_ref_key`
   * collision, `server/app.ts`) leaves a portal row sitting at `status = 'new'`
   * with no booking, in exactly the inbox that sentence said it could not be in.
   * What is true NOW is a different mechanism and a stronger one: portal rows
   * are excluded SERVER-SIDE by `source` (`listInquiries` and
   * `listInboxInquiries`), so neither the badge nor the list can carry one to
   * this door at all.
   * ⛔ TRIGGER: a portal request that lands as an UNNORMALISED inquiry (a
   * crossing that creates the row and fails to book), or `source` joining the
   * inbox projection. Then set it here and this door behaves like the other.
   */
  homeReturnTrip?: boolean | null;
  /**
   * ⭐ T1871 M5 (P0-4) — the count the CUSTOMER typed on the public form
   * (`inquiries.passengers_exact`, already in `INQUIRY_COLUMNS` and already
   * returned whole by `handleGetInquiries`; this is the client half that was
   * missing, not a new route).
   *
   * OPTIONAL for the same reason as the four above: several specs build full
   * `InquiryPrefill` literals and a required field would red every one. Its one
   * producer is `quoteThisInquiry`, which always sets it.
   *
   * ⛔ `null` is NOT CAPTURED, never "unverified". It is null on every legacy
   * row and on every nursing-home crossing by decision, so nothing downstream
   * may gate on its presence. Read with `!= null`; `Number(null) === 0`.
   */
  passengersExact?: number | null;
  /** T384 extra.town — seeds the location search when unresolved. */
  townSeed: string | null;
  /** Routing key from the address-end Eircode (suggestion-grade area hint). */
  routingKey: string | null;
};

/**
 * Inquiry-sourced job start: a FULL reset, then EVERY inquiry field lands in
 * the store (never into the DOM — the venue layer-1 class dies here; C-family
 * completeness is proven by test). `customerFlags` freezes the customer's
 * stated flags as the immutable Edited-badge baseline.
 */
export function sourceFromInquiry(state: AppState, inq: InquiryPrefill): AppState {
  const fresh = resetForNewQuote(state);
  return {
    ...fresh,
    job: {
      ...fresh.job,
      service: inq.service,
      band: inq.band,
      customerName: inq.name,
      customerPhone: inq.phone,
      // Board 2089 M3: the lead's own country, or the Ireland default. Never a
      // silent Ireland on a lead the site recorded as GB.
      customerPhoneCountry: inq.customerPhoneCountry ?? PHONE_DEFAULT_COUNTRY,
      customerEmail: inq.email,
      // The prefill IS the inquiry's own address, so the provenance bit is true
      // from the start; refreshBookingDetail re-decides it against the server.
      customerEmailFromInquiry: inq.email != null,
      customerEircode: inq.eircode,
      contactPreference: inq.contactPreference,
      flightNumber: inq.flightNumber,
      returnPassengers: inq.returnPassengers ?? null,
      returnDate: inq.returnDate ?? null,
      returnTime: inq.returnTime ?? null,
      returnAt: inq.returnAt ?? null,
      homeReturnTrip: inq.homeReturnTrip ?? null,
      recurring: inq.recurring,
      journeyDate: inq.journeyDate,
      journeyTime: inq.journeyTime,
      venue: inq.venue,
      notes: inq.notes,
      courseText: inq.course,
      collectionPlace: inq.collectionPlace,
      options: { ...EMPTY_OPTIONS, ...inq.options },
      /**
       * ⭐ T1717 M6 (DD6) - THE HOME'S ANSWER IS THE BASELINE WHEN THERE IS ONE.
       *
       * ⚠️ ON THIS DOOR IT ALREADY WAS, WHICH IS WHY THE SPREAD IS CONDITIONAL
       * RATHER THAN UNCONDITIONAL: `inq.options.returnTrip` is
       * `inquiries.return_trip`, which `landPortalCrossing` writes from the
       * nurse's answer and nothing ever updates. The override only rewrites
       * `bookings.return_trip`. So this line changes nothing today and exists so
       * the RULE is stated identically on both prefill doors - see
       * `sourceFromBooking`, where it is load-bearing.
       */
      customerFlags: Object.freeze({
        ...inq.options,
        ...(inq.homeReturnTrip != null ? { returnTrip: inq.homeReturnTrip } : {}),
      }),
      customerBand: inq.band,
      // T1871 M5 (P0-4). The count the customer typed, into the EXISTING
      // "Passengers (exact)" box on Quote — no second input anywhere.
      // ⛔ `?? null`, not a truthiness fallback: absent and 0 are different
      // facts and only one of them is real.
      passengersExact: inq.passengersExact ?? null,
      bookingId: inq.bookingId,
      inquiryId: inq.inquiryId,
      suggestedDuplicateId: inq.suggestedDuplicateId,
      locationId: inq.location?.id ?? null,
      locationLabel: inq.location?.label ?? null,
      toLocationId: inq.toLocation?.id ?? null,
      toLocationLabel: inq.toLocation?.label ?? null,
      pickupSearchText: inq.location ? null : inq.pickupText,
      dropoffSearchText: inq.toLocation ? null : inq.dropoffText,
      pickupTerminal: inq.pickupTerminal ?? null,
      customerStops: [...inq.stops],
      // The working capture rows seed from the statement — Helen edits copies,
      // the statement itself stays immutable (369 discipline).
      operatorStops: inq.stops.map((s) => ({ address: s.address, eircode: s.eircode ?? '' })),
      pickupEircode: inq.pickupEircode ?? '',
      dropoffEircode: inq.dropoffEircode ?? '',
      pickupAddressLine: inq.pickupAddressLine ?? '',
      dropoffAddressLine: inq.dropoffAddressLine ?? '',
      pickupTown: inq.pickupTown,
      dropoffTown: inq.dropoffTown,
      locationSearchSeed: inq.location ? null : inq.townSeed,
      routingKey: inq.routingKey,
      prefillSource: 'inquiry',
    },
  };
}

/**
 * The furthest-ready screen for a prefilled job (incumbent parity): manual
 * services skip location; a resolved location + band lands on add-ons.
 *
 * A screen is only skipped on a VALID band, never a truthy one. The public
 * form offers a band the engine rejects ('16+'), and skipping on truthiness
 * would sail such a lead past the only screen that can fix it, into a job
 * that cannot be priced and cannot be navigated back into. `validBands` is
 * threaded in (not imported) to keep this core pure and registry-free.
 */
export function furthestReadyScreen(
  job: JobState,
  pricingMode: string,
  validBands: readonly string[],
): ScreenId {
  const bandReady = job.band !== null && validBands.includes(job.band);
  if (pricingMode === 'manual') return bandReady ? 'quote' : 'passengers';
  // An end is ready with a resolved id OR a recorded off-gazetteer place (T373
  // M4's escape hatch). Without the marker, a hand-quoted Howth journey would be
  // walled at the location screen forever — its only exit was a search that
  // returns nothing, which is what forced the Avoca substitution.
  const pickupReady = job.locationId !== null || job.pickupOffGazetteer;
  const dropoffReady = job.toLocationId !== null || job.dropoffOffGazetteer;
  // T906 S1 — GOLF IS TWO-ENDED and stops sharing the single-ended grid arm.
  // Its picker now states a place at each end, and an off-list pair has NO
  // locationId at all, so the grid arm's `pickupReady` would wave a job through
  // with no destination — which persists one stop and tells the customer where
  // the bus is going by omission. `locationId !== null` stays as the second
  // disjunct so a job re-opened from a booking written before this shipped (its
  // ends recovered from stops, or not recoverable at all) is never WALLED here:
  // a picked route already names both ends inside its composite label.
  const golfReady =
    (job.golfPickupPlace !== null && job.golfDestinationPlace !== null) || job.locationId !== null;
  const locationReady =
    job.service === 'golf'
      ? golfReady
      : pricingMode === 'p2p'
        ? pickupReady && dropoffReady
        : pickupReady;
  if (!locationReady) return 'location';
  if (!bandReady) return 'passengers';
  return 'addons';
}

/** Subset of a booking detail the store consumes on re-open. */
export type BookingPrefill = {
  bookingId: number;
  service: string;
  band: string | null;
  name: string;
  phone: string;
  journeyDate: string | null;
  journeyTime: string | null;
  venue: string | null;
  notes: string | null;
  /** T484 — carried on re-open too, or the Prefers row BookingDetail just
   *  showed would vanish the moment Helen opened the same job in the
   *  calculator (CustomerInfoBlock renders on prefillSource 'booking' as
   *  well as 'inquiry'). */
  contactPreference: string | null;
  options: Partial<QuoteOptions>;
  /** MIDDLE booking_stops (T400: rich — the re-open path previously dropped
   *  the stored eircodes, breaking the round-trip client-side). */
  stops: readonly CustomerStop[];
  /** Flight from the latest linked inquiry (read-only join on the detail). */
  flight: string | null;
  /** ⭐ T1717 M6 (DD5) - `bookings.return_at`, read by its own one-column
   *  function on the detail route. OPTIONAL: several specs build full
   *  `BookingPrefill` literals, and omission is byte-identical to null. */
  returnAt?: string | null;
  /** ⭐ board 2871 (Captain's ruling, C1) - the customer's own return text from
   *  the site form (board 944), off the detail route, so a RE-OPENED web job keeps
   *  its Return row and the Quote screen's Return date defaults to the stated day.
   *  OPTIONAL for the reason `returnAt` above states; omission is null. */
  returnDate?: string | null;
  returnTime?: string | null;
  returnPassengers?: string | null;
  /** ⭐⭐ T2088 M7 (D5) - the HOME's marker, off the detail route's
   *  `portalRequest` (the same durable `inquiries` row `homeReturnTrip` comes
   *  from, not a second read). OPTIONAL for the reason `returnAt` above states,
   *  and omission is byte-identical to `'unset'` - the value every non-portal
   *  booking has. */
  returnAtState?: 'unset' | 'to-confirm';
  /** ⭐ T1717 M6 (DD6) - what the HOME said, from the portal-sourced inquiry.
   *  null when this booking did not come from the portal. OPTIONAL for the same
   *  reason as `returnAt` above. */
  homeReturnTrip?: boolean | null;
  /**
   * ⭐⭐ T1871 M6 (ux-review) - `bookings.passengers_exact`, off the detail
   * route's own one-column read. OPTIONAL for the same reason as `returnAt`:
   * several specs build full `BookingPrefill` literals, and omission has to be
   * byte-identical to "not captured".
   */
  passengersExact?: number | null;
  /** ⭐ board 2814 M7 (DD-7) - the run was passed to a taxi firm, so the
   *  calculator hides the team alert (`/api/notify/send` takes a message body
   *  only, so the server cannot gate it). OPTIONAL: absent reads as not passed. */
  taxiPassed?: boolean;
};

/**
 * Re-open an existing booking in the calculator (R15 A3: real persisted
 * flags restored, never hardcoded false). The persisted flags ARE the
 * baseline: they carry the customer's original statement forward.
 *
 * ⭐⭐ `passengersExact` IS RESTORED (T1871 M6 ux-review), AND THE PARAGRAPH
 * THAT USED TO SIT HERE ARGUING THE OPPOSITE IS DELETED RATHER THAN LEFT
 * STANDING. Its premise was that the value "would have to arrive on
 * `ApiBookingDetail`", which the same milestone then made it do: the detail
 * route reads it through `getBookingPassengersExact` - a one-column read that
 * does NOT widen getBooking's ruled SELECT, so the D3 boundary the old note was
 * protecting is intact and the cost it feared (a route, a fetch per re-open) is
 * already paid for other reasons.
 *
 * ⛔ AND THE RESIDUAL IT CALLED "a display gap, not data loss" WAS BIGGER THAN
 * THAT. Measured live on a re-opened over-band job (band `12-16`, stored count
 * 22): the box came back blank AND THE OVER-BAND MISMATCH BANNER DID NOT
 * REAPPEAR, because that warning is derived from the in-memory count. So the P0-3
 * safety net this milestone exists to provide was present on the first quote and
 * gone on every edit pass afterwards. Restoring the fact restores the warning.
 *
 * ⛔ RESTORED AS A FACT, NEVER AS AN ACT. It does not re-derive the band: this
 * function writes the job object directly rather than through `setBand`'s paired
 * writer, so the PERSISTED band stays authoritative on re-open (the `customerBand`
 * baseline rule, and `savedOverlay`'s) and `bandPinnedByOperator` stays false -
 * the count and the band are allowed to DISAGREE here, which is precisely what
 * makes the mismatch banner fire instead of silently moving Helen's price.
 *
 * ⛔ AND IT CANNOT LEAK INTO A DIFFERENT JOB. The value comes from `b`, the
 * prefill for THIS booking, never from `fresh.job` - and `resetForNewQuote` above
 * has already cleared the field to null, so re-opening a booking with no stored
 * count answers null even when the previous job on this screen had one. Both
 * pinned.
 *
 * ⚠️ `customerPhoneCountry` IS DELIBERATELY NOT CARRIED (board 2089 M3, security
 * review Low), and it is safe for TWO reasons that must BOTH hold. (1) The number
 * this function receives is the STORED one — `actions.ts` prefills
 * `customer.phone_e164` — and a leading `+` overrides the region on every read,
 * so a GB booking re-opened under the Ireland default still parses as GB.
 * (2) `CallInCapture` does not mount on a re-opened booking (`isWalkIn` is false
 * once `bookingId` is set), so there is no picker to disagree with the job.
 * ⇒ REOPENS THE MOMENT EITHER STOPS HOLDING: a prefill source that hands this
 * function a NATIONAL-format number, or a screen that mounts the picker on a
 * re-opened booking. Carry the stored country here then; a `BookingPrefill` key
 * and the `bookings`/`customers` read behind it are what it would cost. Until
 * then the residual degrades VISIBLY (the read-back says which country it failed
 * under) rather than silently.
 */
export function sourceFromBooking(state: AppState, b: BookingPrefill): AppState {
  const fresh = resetForNewQuote(state);
  return {
    ...fresh,
    job: {
      ...fresh.job,
      service: b.service,
      band: b.band,
      customerName: b.name,
      customerPhone: b.phone,
      journeyDate: b.journeyDate,
      journeyTime: b.journeyTime,
      venue: b.venue,
      notes: b.notes,
      contactPreference: b.contactPreference,
      options: { ...EMPTY_OPTIONS, ...b.options },
      /**
       * ⭐⭐ T1717 M6 (DD6, P0-11) - THE HOME'S ANSWER IS THE BASELINE, AND ON
       * THIS DOOR THAT IS A REAL CHANGE RATHER THAN A RESTATEMENT.
       *
       * ⛔ `b.options.returnTrip` IS `bookings.return_trip`, WHICH THE OPERATOR
       * OVERWRITES. `updateBookingFlags` writes it on every quote save and every
       * park, so after Helen's first override the "immutable customer baseline"
       * this object exists to hold would BE her own value: the `Edited` chip
       * would clear itself, the warn banner would never fire twice, and the
       * booking would quietly forget what the nursing home asked for.
       *
       * ⛔ SO THE ANSWER COMES FROM `inquiries.return_trip` (via the detail
       * route's dedicated read), which the crossing writes once and nothing
       * updates. `homeReturnTrip` null means no portal answer exists - a web
       * enquiry or a walk-in - and then the booking's own flag stays the
       * baseline, exactly as before.
       * ⛔ THE FLAG ONLY. Nothing else in this bag is re-baselined: DD6 rules the
       * override audit field-scoped to `returnTrip`, and a blanket flag-bag
       * baseline would drag `accessibleVehicle` - Article 9 - along with it.
       */
      customerFlags: Object.freeze({
        ...b.options,
        ...(b.homeReturnTrip != null ? { returnTrip: b.homeReturnTrip } : {}),
      }),
      customerBand: b.band,
      customerStops: [...b.stops],
      operatorStops: b.stops.map((s) => ({ address: s.address, eircode: s.eircode ?? '' })),
      returnAt: b.returnAt ?? null,
      // ⭐ board 2871 (C1): the customer's stated return, carried on re-open too.
      returnDate: b.returnDate ?? null,
      returnTime: b.returnTime ?? null,
      returnPassengers: b.returnPassengers ?? null,
      // ⭐⭐ T2088 M7 (D5). `?? 'unset'` absorbs an absent key (an older server,
      // a non-portal booking, or one of the hand-built prefill fixtures) as
      // SILENCE, never as a promise - the same fail-closed direction the seam
      // projection and `getPortalReturnAnswer` both coerce in.
      returnAtState: b.returnAtState ?? 'unset',
      homeReturnTrip: b.homeReturnTrip ?? null,
      // ⛔ FROM `b`, NEVER FROM `fresh.job` - see the leak note in the docblock.
      // `?? null` because the field is optional on the prefill: an omitted key
      // and a null one both mean NOT CAPTURED, never 0.
      passengersExact: b.passengersExact ?? null,
      flightNumber: b.flight,
      bookingId: b.bookingId,
      // ⭐ board 2814 M7: from `b`, never `fresh.job` (EMPTY_JOB has no key), so it
      // cannot cross jobs; and only when true, so every other re-open is unchanged.
      ...(b.taxiPassed === true ? { taxiPassed: true } : {}),
      prefillSource: 'booking',
    },
  };
}

// ---------------------------------------------------------------------------
// Quote request sequencing (monotonic racing guard — incumbent parity)
// ---------------------------------------------------------------------------

/** Issue a new quote request: bumps the monotonic counter and returns the
 *  seq the caller must present back to `applyQuoteResult`. */
export function beginQuoteRequest(state: AppState): { state: AppState; seq: number } {
  const seq = state.quoteRequestSeq + 1;
  return { state: { ...state, quoteRequestSeq: seq }, seq };
}

/**
 * Apply a quote response IFF it belongs to the LATEST issued request —
 * a stale (raced) response is discarded, never rendered. The guard is
 * strictly-latest, not merely newer-than-applied (incumbent parity).
 */
export function applyQuoteResult(
  state: AppState,
  seq: number,
  result: { finalTotalCents: number | null; wasHandQuoted?: boolean; quote?: QuoteView | null },
): AppState {
  if (seq !== state.quoteRequestSeq) return state;
  return {
    ...state,
    appliedQuoteSeq: seq,
    job: {
      ...state.job,
      finalTotalCents: result.finalTotalCents,
      wasHandQuoted: result.wasHandQuoted ?? false,
      ...(result.quote !== undefined ? { quote: result.quote } : {}),
    },
  };
}

/**
 * Plain job-field write for screen inputs (location picks, band, options,
 * venue, typed name/phone). Invariant-carrying transitions (reset, prefill,
 * quote sequencing) stay named — never re-implement them via patch.
 * `customerFlags` and `customerBand` are deliberately not patchable: they are
 * the customer's ORIGINAL statement, and the Edited badges are only truthful
 * while they cannot be written by a screen input.
 */
export function patchJob(
  state: AppState,
  patch: Partial<Omit<JobState, 'customerFlags' | 'customerBand'>>,
): AppState {
  return { ...state, job: { ...state.job, ...patch } };
}

/* ⛔ `accountBookingId` WAS HERE ("is this job for an organisation?") and went with
   the nursing tile at board 2814 M6 (DD-8): the calculator never holds an account
   job now, and the capture route refuses an `accountId` (400). */

/** Open a booking's detail screen (browse context, job untouched). */
export function openBookingDetail(state: AppState, bookingId: number): AppState {
  return navigate({ ...state, viewBookingId: bookingId }, 'booking-detail');
}

/** ⭐ board 2812 M4 (plan step 2): show a home's page (its group key) or All homes
 *  (`null`) on Nursing homes. NOT a navigation: no history step, no `navSeq`. */
export function openHome(state: AppState, key: string | null): AppState {
  return state.viewHomeKey === key ? state : { ...state, viewHomeKey: key };
}

/** ⭐ board 2812 fix round (item 8): open or close the showing home's Details. NOT a
 *  navigation (no history step, no `navSeq`); the same state back when nothing moves,
 *  so a close on a closed Details re-renders nothing. */
export function setHomeDetails(state: AppState, open: boolean): AppState {
  return state.viewHomeDetails === open ? state : { ...state, viewHomeDetails: open };
}

/**
 * ⭐ board 2812 M4 fix round (L5): the rail's "Nursing homes" row is the area's ENTRY,
 * as the round-2 mockup's entry is: it opens All homes (and App opens it on To do),
 * whichever home was showing before, even from that home's booking. Back from a home's
 * booking never presses the rail, so it still returns to the home (`keepHome`).
 */
export function openNursingFromRail(state: AppState): AppState {
  return setHomeDetails(openHome(navigate(state, 'nursing-homes'), null), false);
}

// ---------------------------------------------------------------------------
// Badge single source (§7.5)
// ---------------------------------------------------------------------------

export function setInboxCount(state: AppState, count: number): AppState {
  return { ...state, inboxCount: count, inboxCountError: false };
}

/** A failed derivation is an ERROR state, never a silent zero (the badge
 *  must "never understate" — R13 class). */
export function setInboxCountError(state: AppState): AppState {
  return { ...state, inboxCountError: true };
}

export function setRateChangesCount(state: AppState, count: number): AppState {
  return { ...state, rateChangesCount: count, rateChangesError: false };
}

export function setPortalCount(state: AppState, count: number): AppState {
  return { ...state, portalCount: count, portalCountError: false };
}

/** A failed portal read is an ERROR state, never a silent zero — same rule as
 *  the two badges above, and the seam this one crosses is the flakiest of the
 *  three (kiosk -> portal over HTTP). */
export function setPortalCountError(state: AppState): AppState {
  return { ...state, portalCountError: true };
}

export function setRateChangesError(state: AppState): AppState {
  return { ...state, rateChangesError: true };
}

// ---------------------------------------------------------------------------
// Per-booking error scope (§7.3)
// ---------------------------------------------------------------------------

export function setBookingError(state: AppState, bookingId: number, message: string): AppState {
  return { ...state, bookingErrors: { ...state.bookingErrors, [bookingId]: message } };
}

export function clearBookingError(state: AppState, bookingId: number): AppState {
  if (!(bookingId in state.bookingErrors)) return state;
  const next = { ...state.bookingErrors };
  delete next[bookingId];
  return { ...state, bookingErrors: next };
}
